/**
 * BrandAxisDashboard — the « Tableau de bord » view of a Brand Perception axis.
 *
 * Design: docs/plans/bp-dashboard.md §5.2 (lot 4). Sibling of
 * `components/conversations/ConversationsDashboard.tsx`, whose idiom this follows:
 * own filter bar, ONE debounced fetch, the shared SVG primitives in `../viz` (which
 * carry no i18n, so every label is passed in).
 *
 * Deliberate differences from the Conversations dashboard:
 *  - ONE period control. Conversations renders a `ToggleButtonGroup` of windows in
 *    the page toolbar AND a `TimeframeSelector` in the dashboard; §5.2 calls that a
 *    wart. The only period control is the `TimeframeSelector` below.
 *  - « Segmenter par » is a SEPARATE control from the filters: segmenting draws one
 *    line per value and removes nothing; a filter narrows the denominator.
 *
 * Three invariants the endpoint imposes on this UI:
 *  1. `breakdowns=true` is what makes the service read a range family at all — the
 *     perception lede, the sentiment / alignment splits, the topics and every `by*`
 *     map are set ONLY inside `applyBreakdowns`. Général is built on those, so it
 *     asks for them; §7's worked example sizes that at 91 items / ~180 KB for a
 *     90-day single-brand window.
 *  2. TWO narrowing dimensions at once is an HTTP 400 (`assertSingleFilter`): the
 *     `by*` rollups are pre-folded dimension by dimension, never cross-tabbed. The
 *     filter bar therefore disables the other dimensions as soon as one is active,
 *     with the reason visible — a request that 400s must be unreachable, not caught.
 *  3. Every `caveat` the response carries is rendered. A narrowing the numbers do
 *     not actually reflect is worse than no narrowing at all.
 *
 * Window ceiling: the FULL retention (730 d, 2026-10-08) — but only the trend line
 * and the KPI row are true that far back. `BPSERIES` carries 730 daily slots on one
 * row, so the score and the three rates fold into months correctly; the breakdown
 * cards read `BPTREND`, whose rows expire at 90 days and whose monthly counterpart
 * has no writer yet, so past 90 days they come back EMPTY with `monthly_tier_empty`
 * on the response. That caveat is rendered.
 *
 * No Cibles sub-tabs since 2026-10-14: Contenu / Produits moved to the « Cibles
 * citées » card of the Vue d'ensemble (`pages/dashboard/TargetCitationsCard.tsx`),
 * one card for the three axes instead of one copy per axis.
 * `resolveTimeframe` is not used here: it subtracts a full `days`, which makes
 * « 90 derniers jours » a 91-day INCLUSIVE span, and the API's `selectGrain` tips at
 * 91 into the monthly tier. See `resolveBpRange`.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import TimeframeSelector from '../common/TimeframeSelector';
// The inclusive-span / 90-day rule, shared with the prompt matrix (lot 8) so the
// two callers of the BP endpoints cannot resolve the same timeframe differently.
import { BP_MAX_SPAN_DAYS as MAX_SPAN_DAYS, resolveBpRange, useBpTimeframe } from './bpRange';
import {
  BarRows,
  CARD,
  ChartCard,
  ColumnBars,
  EmptyNote,
  NoticePanel,
  StackedBars,
  StatTile,
  TrendLine,
  VizTable,
  VIZ,
  bucketK,
  bucketVals,
  calendarGroups,
  fmtInt,
  fmtPct,
  setVizLocale,
  type BarRow,
  type TrendLineSeries,
} from '../viz';
import { api } from '../../services/api';
import { brandQueriesApi } from '../../services/brand-queries';
import {
  BP_DASHBOARD_MAX_OVERLAY_BRANDS,
  BRAND_QUERY_LEVELS,
  type BPDashboardCaveat,
  type BPDashboardResponse,
  type BPDashboardSegmentBy,
  type BrandQueryAxis,
} from '@traaker/shared';
// The engine logo URL for an SVG `<image href>` — the ONE such helper in the app
// (`EngineLogo`'s own map is for a React tree and has no AI-Overview entry). Imported
// rather than copied: a second copy is how the `BRAND_STRATEGY_LIMITS` pair drifted.
import { engineIconSrc } from '../conversations/personaFormat';
// Per-engine BAR colours come from the brands' real marks, never from the mock's
// harmonised set: a logo beside a bar in the wrong brand colour reads as a bug.
import { ENGINE_META, engineKey } from './EngineLogo';
import { languageFlag } from '../../pages/dashboard/geoFlags';
import { Flag } from '../../pages/dashboard/Flag';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * `VIZ.series` is a FIXED-ORDER 4-colour categorical palette and must never be
 * cycled, so at most 4 overlay / segment lines are drawn. The rest stay in the
 * table twin and the chart says how many it left out.
 *
 * ALIASED FROM `@traaker/shared`, never re-declared: the API rejects a 5th overlay
 * on this exact bound, so a local literal that merely agrees today is the drift that
 * silently split the two `BRAND_STRATEGY_LIMITS` copies. Counts OVERLAY lines — the
 * primary brand is additional and renders in `VIZ.ink`.
 */
const MAX_SERIES = BP_DASHBOARD_MAX_OVERLAY_BRANDS;

/** Display names, not copy — engine ids are product names in every language. */
const ENGINE_LABELS: Record<string, string> = {
  perplexity: 'Perplexity',
  openai: 'ChatGPT',
  chatgpt: 'ChatGPT',
  gemini: 'Gemini',
  claude: 'Claude',
  mistral: 'Mistral',
  ai_overview: 'AI Overview',
  google: 'Google AI Overview',
};

/**
 * A language code's own name, from `Intl.DisplayNames` rather than an i18n map: the
 * `language` facet is free-form on a prompt, so any ISO code can turn up and a
 * hand-written map would silently fall back to the bare code for the ones it misses.
 * Unresolvable (or a non-code) → the code upper-cased, never a guess.
 */
function languageLabel(code: string, uiLang: string): string {
  const raw = (code || '').trim();
  if (!raw) return raw;
  try {
    const name = new Intl.DisplayNames([uiLang], { type: 'language' }).of(raw);
    if (name && name.toLowerCase() !== raw.toLowerCase()) {
      return name.charAt(0).toUpperCase() + name.slice(1);
    }
  } catch {
    // Unknown/ill-formed code — Intl throws rather than guessing, and so do we.
  }
  return raw.toUpperCase();
}

const SENTIMENT_COLORS = { positive: VIZ.good, neutral: VIZ.warning, negative: VIZ.critical };
const ALIGNMENT_COLORS = {
  strong: VIZ.good,
  partial: VIZ.warning,
  weak: VIZ.serious,
  misaligned: VIZ.critical,
};

/**
 * Columns the sentiment / alignment charts will draw before coarsening the calendar
 * grain. Ten 100%-stacked columns is about where the inline share labels and the
 * per-column response counts stop fitting side by side.
 */
const SPLIT_MAX_COLUMNS = 10;

/** Which narrowing dimensions the filter bar offers, in order. `level` is referral-only. */
type FilterDim = 'tags' | 'engine' | 'zone' | 'language' | 'level';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Percentage-POINT delta for a rate tile, or `undefined` when nothing compares. */
function pointDelta(now: number | null | undefined, before: number | null | undefined) {
  if (now == null || before == null) return undefined;
  return (now - before) * 100;
}

function scoreDelta(now: number | null | undefined, before: number | null | undefined) {
  if (now == null || before == null) return undefined;
  return now - before;
}

function LoadingSkeleton() {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rounded" height={140} />
        ))}
      </Box>
      <Skeleton variant="rounded" height={320} />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
        <Skeleton variant="rounded" height={240} />
        <Skeleton variant="rounded" height={240} />
      </Box>
    </Box>
  );
}

/** Flat panel used for the two lot-7 sub-tabs and for a hard load error. */
function NotePanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: `${CARD.padY * 8}px ${CARD.padX * 8}px`,
        borderRadius: `${CARD.radius}px`,
        border: `1px solid ${VIZ.border}`,
        boxShadow: 'none',
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600, color: VIZ.ink, mb: 0.75 }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: 13.5, lineHeight: 1.45, color: VIZ.muted }}>
        {children}
      </Typography>
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface BrandAxisDashboardProps {
  brandId: string;
  axis: BrandQueryAxis;
}

export default function BrandAxisDashboard({ brandId, axis }: BrandAxisDashboardProps) {
  const { t, i18n } = useTranslation();
  setVizLocale(i18n.language);

  // Shared with the prompt matrix in « Configurer » and with the other two axes —
  // the selection survives every move between those screens (`useBpTimeframe`).
  const [timeframe, setTimeframe] = useBpTimeframe();

  const [tags, setTags] = useState<string[]>([]);
  const [engine, setEngine] = useState('');
  const [zone, setZone] = useState('');
  const [language, setLanguage] = useState('');
  const [level, setLevel] = useState('');
  const [segmentBy, setSegmentBy] = useState<BPDashboardSegmentBy>('none');

  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);

  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [data, setData] = useState<BPDashboardResponse | null>(null);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => resolveBpRange(timeframe), [timeframe]);

  /** The single active narrowing dimension, or `null`. Drives the mutual disable. */
  const activeDim: FilterDim | null = tags.length
    ? 'tags'
    : engine
      ? 'engine'
      : zone
        ? 'zone'
        : language
          ? 'language'
          : level
            ? 'level'
            : null;
  const dimLabel: Record<FilterDim, string> = {
    tags: t('bpDash.dimTags'),
    engine: t('bpDash.dimEngine'),
    zone: t('bpDash.dimZone'),
    language: t('bpDash.dimLanguage'),
    level: t('bpDash.dimLevel'),
  };
  const lockedBy = (dim: FilterDim) =>
    activeDim && activeDim !== dim ? t('bpDash.disabledByFilter', { dim: dimLabel[activeDim] }) : '';

  /**
   * What is active, surfaced OUTSIDE the popover. At most one dimension can narrow at
   * a time (the API rejects two), but `tags` is multi-valued, so the count is the
   * number of narrowing VALUES — which is also what the `tags_or_overlap` caveat is
   * about.
   */
  const activeFilterChips = [
    ...tags.map((v) => ({
      key: `tag:${v}`,
      label: `${dimLabel.tags} : ${v}`,
      clear: () => setTags(tags.filter((x) => x !== v)),
    })),
    ...(engine ? [{ key: 'engine', label: `${dimLabel.engine} : ${ENGINE_LABELS[engine] ?? engine}`, clear: () => setEngine('') }] : []),
    ...(zone ? [{ key: 'zone', label: `${dimLabel.zone} : ${zone}`, clear: () => setZone('') }] : []),
    ...(language ? [{ key: 'language', label: `${dimLabel.language} : ${languageLabel(language, i18n.language)}`, clear: () => setLanguage('') }] : []),
    ...(level ? [{ key: 'level', label: `${dimLabel.level} : ${level}`, clear: () => setLevel('') }] : []),
  ];
  const activeFilterCount = activeFilterChips.length;
  const clearFilters = () => {
    setTags([]);
    setEngine('');
    setZone('');
    setLanguage('');
    setLevel('');
  };

  // ---- option sources -----------------------------------------------------

  useEffect(() => {
    if (!brandId) return;
    brandQueriesApi
      .listTags(brandId)
      .then((r) => setTagOptions((r?.tags ?? []).map((x) => x.name)))
      .catch(() => setTagOptions([]));
  }, [brandId]);

  // ---- the one fetch ------------------------------------------------------

  useEffect(() => {
    if (!brandId) return;
    const params = new URLSearchParams({ axis, from: range.from, to: range.to });
    // Général is built ON the breakdowns (perception lede, sentiment, topics, by*).
    params.set('breakdowns', 'true');
    // No `brands` param: the « Marques comparées » control was removed (2026-10-08),
    // so no overlay is ever requested. `scoreSeries` still renders `byBrand` when the
    // response carries it — the API contract is unchanged, only this caller's use of it.
    if (tags.length) params.set('tags', tags.join(','));
    if (engine) params.set('engine', engine);
    if (zone) params.set('zone', zone);
    if (language) params.set('language', language);
    if (level) params.set('level', level);
    if (segmentBy !== 'none') params.set('segmentBy', segmentBy);

    const timer = setTimeout(() => {
      setLoading(true);
      api
        .get<BPDashboardResponse>(`/brands/${brandId}/brand-perception/dashboard?${params}`)
        .then((resp) => {
          setData(resp ?? null);
          setLoadError('');
        })
        .catch((err: any) => {
          setData(null);
          setLoadError(err?.message || t('bpDash.loadError'));
        })
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId, axis, range.from, range.to, tags, engine, zone, language, level, segmentBy]);

  // ---- derived view models ------------------------------------------------

  const notMeasured = t('bpDash.notMeasured');
  const unmeasured = t('bpDash.notMeasuredShort');
  const emptyNote = t('bpDash.noData');
  const viewLabels = { chartLabel: t('bpDash.viewChart'), tableLabel: t('bpDash.viewTable') };
  const dayCol = data?.grain === 'month' ? t('bpDash.colMonth') : t('bpDash.colDay');

  const fmtBucket = (key: string) => {
    if (!key) return '';
    if (key.length === 7) {
      return new Date(`${key}-01T00:00:00Z`).toLocaleDateString(i18n.language, {
        month: 'short',
        year: 'numeric', // « nov. 2026 », never « nov. 26 » (read as a day)
        timeZone: 'UTC',
      });
    }
    return new Date(`${key}T00:00:00Z`).toLocaleDateString(i18n.language, {
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
  };

  const points = data?.points ?? [];
  const k = bucketK(points.length);
  const bucketLabels = useMemo(() => {
    const out: string[] = [];
    for (let i = 0; i < points.length; i += k) out.push(fmtBucket(points[i].day));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, k, i18n.language]);

  /**
   * Columns for the part-of-whole charts (sentiment, alignment), grouped by the
   * coarsest CALENDAR grain that stays readable — day, else week, else month.
   *
   * Not `bucketK`: `points` carries only MEASURED buckets, so folding every k-th
   * point put 28 juil. next to 29 sept. in two equal columns and the axis then
   * claimed they were adjacent in time (2026-10-08). A calendar key makes a column
   * mean a real span, and a 90-day window of daily audits becomes 3 month columns
   * instead of 90 unreadable ones.
   */
  const splitGroups = useMemo(
    () => calendarGroups(points.map((p) => p.day), SPLIT_MAX_COLUMNS),
    [points],
  );
  const groupLabel = (key: string) =>
    splitGroups.grain === 'week'
      ? t('bpDash.weekOf', { date: fmtBucket(key) })
      : fmtBucket(key);

  const segLabel = (key: string) =>
    segmentBy === 'engine'
      ? (ENGINE_LABELS[key] ?? key)
      : segmentBy === 'language'
        ? languageLabel(key, i18n.language)
        : key;

  /**
   * Score lines. The primary brand wears ink (it is not one category among others);
   * overlays / segments take the categorical palette in order and are capped at
   * `MAX_SERIES` so no hue is reused.
   */
  const scoreSeries = useMemo<TrendLineSeries[]>(() => {
    if (!points.length) return [];
    const primary: TrendLineSeries = {
      key: '__primary',
      // Brand violet, and the only line that gets the area fill below it — the
      // brand's own score is the subject of the card, not one category among others.
      color: VIZ.violet,
      label: t('bpDash.seriesScore'),
      width: 2.5,
      points: bucketVals(points.map((p) => p.score), k),
    };
    // F step 8b (F-D7): the Image per-criterion score (`image_v1` rows) is its own
    // dashed line, plotted on the buckets that carry it only, never folded into
    // `score` and never drawn at 0 elsewhere.
    const imageV1: TrendLineSeries[] = points.some((p) => p.scoreImageV1 != null)
      ? [
          {
            key: '__scoreImageV1',
            color: VIZ.violet,
            label: t('bpDash.seriesScoreImageV1'),
            width: 2,
            dashed: true,
            points: bucketVals(points.map((p) => p.scoreImageV1 ?? null), k),
          },
        ]
      : [];
    // `byBrand` already carries the display name, so it wins over `bySegment` when
    // the segmentation IS the brand axis — both are populated in that case.
    const overlays = Object.entries(data?.byBrand ?? {}).filter(([id]) => id !== brandId);
    if (overlays.length) {
      return [
        primary,
        ...imageV1,
        ...overlays.slice(0, MAX_SERIES).map(([id, v], i) => ({
          key: id,
          label: v.brandName,
          color: VIZ.series[i],
          points: bucketVals(v.points, k),
        })),
      ];
    }
    const segs = Object.entries(data?.bySegment ?? {}).sort(
      (a, b) => b[1].promptCount - a[1].promptCount,
    );
    if (segs.length) {
      return [
        primary,
        ...imageV1,
        ...segs.slice(0, MAX_SERIES).map(([key, v], i) => ({
          key,
          label: segLabel(key),
          color: VIZ.series[i],
          points: bucketVals(v.points, k),
        })),
      ];
    }
    return [primary, ...imageV1];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, k, data?.byBrand, data?.bySegment, brandId, segmentBy, i18n.language]);

  /** F step 8b: a dotted rule at the window's first bucket scored under `image_v1`. */
  const ruleBoundary = useMemo(() => {
    const line = scoreSeries.find((s) => s.key === '__scoreImageV1');
    const index = line ? line.points.findIndex((v) => v != null) : -1;
    return index >= 0 ? [{ index, label: t('bpDash.ruleBoundary'), dotted: true }] : [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scoreSeries, i18n.language]);

  const hiddenSegments = useMemo(() => {
    const overlays = Object.keys(data?.byBrand ?? {}).filter((id) => id !== brandId).length;
    const segs = Object.keys(data?.bySegment ?? {}).length;
    const n = overlays || segs;
    return n > MAX_SERIES ? n - MAX_SERIES : 0;
  }, [data?.byBrand, data?.bySegment, brandId]);

  /**
   * The rate lines live on their own chart: a 0-1 scale cannot share the score's axis.
   *
   * Colours are per FIELD, not positional: citation is green and recommendation is
   * orange on the KPI dot, on the KPI bar and on this line, so the three readings of
   * one measurement cannot disagree. A positional `VIZ.series[i]` would recolour
   * recommendation the day an axis stops reporting a citation rate.
   */
  const rateSeries = useMemo<TrendLineSeries[]>(() => {
    if (!points.length) return [];
    const defs: { field: 'citationRate' | 'recommendationRate' | 'sourceRate'; label: string; color: string }[] = [
      { field: 'citationRate', label: t('bpDash.kpiCitationRate'), color: VIZ.good },
      { field: 'recommendationRate', label: t('bpDash.kpiRecommendationRate'), color: VIZ.serious },
      { field: 'sourceRate', label: t('bpDash.kpiSourceRate'), color: VIZ.violet },
    ];
    return defs
      .filter((d) => points.some((p) => p[d.field] != null))
      .map((d) => ({
        key: d.field,
        label: d.label,
        color: d.color,
        points: bucketVals(points.map((p) => p[d.field] ?? null), k),
      }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points, k, i18n.language]);

  /**
   * Sentiment / alignment are index-aligned to `points`, so they fold over the SAME
   * buckets. A bucket with no counts is dropped, not drawn at zero. `StackedBars`
   * wants shares, so the division happens once, here.
   */
  const splitRows = (
    series: (Record<string, number> | null)[] | undefined,
    parts: { key: string; label: string; color: string }[],
  ) => {
    if (!series?.length) return [];
    const out: { label: string; n: number; parts: { key: string; label: string; value: number; color: string }[] }[] = [];
    for (const g of splitGroups.groups) {
      const sums = parts.map((p) =>
        g.idx.reduce((sum, i) => sum + (series[i]?.[p.key] ?? 0), 0),
      );
      const total = sums.reduce((a, b) => a + b, 0);
      // A group with no counts produces NO column — never a zero one. The same rule
      // as a broken line: nothing measured is not a measurement of nothing.
      if (total === 0) continue;
      out.push({
        label: groupLabel(g.key),
        n: total,
        // Shares of the GROUP's own total, so a 50-response day and a 75-response
        // day inside one month contribute in proportion rather than equally.
        parts: parts.map((p, pi) => ({ ...p, value: sums[pi] / total })),
      });
    }
    return out;
  };

  const sentimentRows = useMemo(
    () =>
      splitRows(data?.sentiment as (Record<string, number> | null)[] | undefined, [
        { key: 'positive', label: t('axisPage.sentiment.positive'), color: SENTIMENT_COLORS.positive },
        { key: 'neutral', label: t('axisPage.sentiment.neutral'), color: SENTIMENT_COLORS.neutral },
        { key: 'negative', label: t('axisPage.sentiment.negative'), color: SENTIMENT_COLORS.negative },
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.sentiment, points, splitGroups, i18n.language],
  );

  const alignmentRows = useMemo(
    () =>
      splitRows(data?.alignment as (Record<string, number> | null)[] | undefined, [
        { key: 'strong', label: t('axisPage.alignment.strong'), color: ALIGNMENT_COLORS.strong },
        { key: 'partial', label: t('axisPage.alignment.partial'), color: ALIGNMENT_COLORS.partial },
        { key: 'weak', label: t('axisPage.alignment.weak'), color: ALIGNMENT_COLORS.weak },
        { key: 'misaligned', label: t('axisPage.alignment.misaligned'), color: ALIGNMENT_COLORS.misaligned },
      ]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.alignment, points, splitGroups, i18n.language],
  );

  /**
   * `by*` map → `BarRows` rows on a 0-100 score scale, highest first, holes kept.
   *
   * `mark` and `color` are resolved from the RAW key (an engine id, a language code)
   * while `label` is the human name — so a mark-only row never loses its name, it
   * just moves it to the tooltip and the table twin.
   */
  type ScoreBucket = { score: number | null; n?: number; promptCount?: number };
  const scoreRows = (
    map: Record<string, ScoreBucket> | undefined,
    opts: {
      label: (key: string) => string;
      order?: readonly string[];
      mark?: (key: string) => ReactNode;
      color?: (key: string) => string;
      /** Second line under the name. Omit to put the count in `valueLabel` instead. */
      sub?: (n: number | undefined) => string | undefined;
      /** Richer right column (« 23 / 100 · 10 réponses »). Default: the bare score. */
      withScale?: (n: number | undefined) => string;
    },
  ): BarRow[] => {
    if (!map) return [];
    const entries = Object.entries(map);
    const sorted = opts.order
      ? entries.sort((a, b) => opts.order!.indexOf(a[0]) - opts.order!.indexOf(b[0]))
      : entries.sort((a, b) => (b[1].score ?? -1) - (a[1].score ?? -1));
    return sorted.map(([key, v]) => {
      const n = v.n ?? v.promptCount;
      return {
        key,
        label: opts.label(key),
        sub: opts.sub?.(n),
        value: v.score,
        color: opts.color?.(key),
        mark: opts.mark?.(key),
        valueLabel:
          v.score == null ? (
            unmeasured
          ) : opts.withScale ? (
            <>
              <b>{fmtInt(v.score)}</b> / 100 · {opts.withScale(n)}
            </>
          ) : (
            <b>{fmtInt(v.score)}</b>
          ),
      };
    });
  };

  const scoreTable = (rows: BarRow[], nCol: string, counts: Record<string, ScoreBucket> | undefined) => (
    <VizTable
      columns={[t('bpDash.colLabel'), t('bpDash.colScore'), nCol]}
      align={['left', 'right', 'right']}
      rows={rows.map((r) => {
        const b = counts?.[r.key];
        const n = b?.n ?? b?.promptCount;
        return [
          <Box key="l" component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            {r.mark}
            {r.label}
          </Box>,
          r.value != null ? fmtInt(r.value) : <Box component="span" sx={{ color: VIZ.nodata }}>{unmeasured}</Box>,
          n != null ? fmtInt(n) : <Box component="span" sx={{ color: VIZ.nodata }}>{unmeasured}</Box>,
        ];
      })}
    />
  );

  /** Engine marks: the logo, at the 22px the mock gives a leading mark. */
  const engineMark = (id: string) => {
    const src = engineIconSrc(id);
    return src ? (
      <Box
        component="img"
        src={src}
        alt=""
        sx={{ width: 18, height: 18, objectFit: 'contain', flex: 'none' }}
      />
    ) : undefined;
  };
  const engineColor = (id: string) => ENGINE_META[engineKey(id)].color;

  // ---- caveats ------------------------------------------------------------

  const caveatLines = useMemo(() => {
    const out: string[] = [];
    if (range.spanClamped) out.push(t('bpDash.caveat.spanClamped', { days: fmtInt(MAX_SPAN_DAYS) }));
    if (data?.clamped) out.push(t('bpDash.caveat.clamped'));
    for (const c of data?.caveats ?? []) out.push(t(`bpDash.caveat.${c}`));
    // Genuinely-computed counts, so `0` here is a measurement and says nothing worth
    // printing; only a non-zero one is a caveat about tag coverage.
    if (data?.unknownPrompts) out.push(t('bpDash.caveat.unknownPrompts', { n: fmtInt(data.unknownPrompts) }));
    if (data?.untaggedPrompts) out.push(t('bpDash.caveat.untaggedPrompts', { n: fmtInt(data.untaggedPrompts) }));
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.caveats, data?.clamped, data?.unknownPrompts, data?.untaggedPrompts, range.spanClamped, i18n.language]);

  const hasCaveat = (c: BPDashboardCaveat) => (data?.caveats ?? []).includes(c);

  // ---- filter bar ---------------------------------------------------------

  const engineOptions = useMemo(() => Object.keys(data?.byEngine ?? {}).sort(), [data?.byEngine]);
  const zoneOptions = useMemo(() => Object.keys(data?.byZone ?? {}).sort(), [data?.byZone]);
  const languageOptions = useMemo(
    () => Object.keys(data?.byLanguage ?? {}).sort(),
    [data?.byLanguage],
  );

  /**
   * `'brand'` is deliberately absent: it needs `brands[]` populated to mean anything
   * (`wantOverlay = q.brands.length > 0 || segmentBy === 'brand'`, and the overlay ids
   * come from `brands[]`), and the control that populated it was removed. Offering it
   * would draw the primary line alone and read as "no other brand differs". The API
   * still accepts the value — this is a UI decision, not a contract change.
   */
  const SEGMENT_OPTIONS: BPDashboardSegmentBy[] = [
    'none',
    'tag',
    'engine',
    'zone',
    'language',
    ...(axis === 'referral' ? (['level'] as BPDashboardSegmentBy[]) : []),
  ];
  const segmentOptionLabel: Record<BPDashboardSegmentBy, string> = {
    none: t('bpDash.segmentNone'),
    tag: t('bpDash.segmentTag'),
    engine: t('bpDash.segmentEngine'),
    zone: t('bpDash.segmentZone'),
    language: t('bpDash.segmentLanguage'),
    level: t('bpDash.segmentLevel'),
    brand: t('bpDash.segmentBrand'),
  };

  /** A select whose only job is "all / one value", locked when another dim is active. */
  const dimSelect = (
    dim: FilterDim,
    value: string,
    onChange: (v: string) => void,
    options: string[],
    allLabel: string,
    label: string,
    optionLabel: (v: string) => string = (v) => v,
  ) => {
    const reason = lockedBy(dim);
    return (
      <Tooltip title={reason} disableHoverListener={!reason}>
        <TextField
          select
          size="small"
          label={label}
          value={value}
          disabled={!!reason || (options.length === 0 && !value)}
          onChange={(e) => onChange(e.target.value)}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="">{allLabel}</MenuItem>
          {options.map((o) => (
            <MenuItem key={o} value={o}>
              {optionLabel(o)}
            </MenuItem>
          ))}
        </TextField>
      </Tooltip>
    );
  };

  /**
   * The narrowing controls, now behind the « Filtres » button.
   *
   * « Marques comparées » is GONE (2026-10-08, product call). With it goes the
   * `brand` segmentation: `wantOverlay` needs `brands[]` to have something in it, so
   * « Segmenter par : Marque » with no brand picker would draw the primary line alone
   * and silently answer "no other brand differs" — see `SEGMENT_OPTIONS`.
   */
  const generalFilters = (
    <>
      <Tooltip title={lockedBy('tags')} disableHoverListener={!lockedBy('tags')}>
        <Autocomplete<string, true>
          multiple
          size="small"
          disabled={!!lockedBy('tags')}
          options={tagOptions}
          value={tags}
          onChange={(_, v) => setTags(v)}
          renderInput={(params) => (
            <TextField {...params} label={t('bpDash.filterTags')} placeholder={t('bpDash.allTags')} />
          )}
          sx={{ minWidth: 200, maxWidth: 320 }}
        />
      </Tooltip>

      {dimSelect('engine', engine, setEngine, engineOptions, t('bpDash.allEngines'), t('bpDash.filterEngine'), (v) => ENGINE_LABELS[v] ?? v)}
      {dimSelect('zone', zone, setZone, zoneOptions, t('bpDash.allZones'), t('bpDash.filterZone'))}
      {dimSelect('language', language, setLanguage, languageOptions, t('bpDash.allLanguages'), t('bpDash.filterLanguage'), (v) => languageLabel(v, i18n.language))}
      {axis === 'referral' &&
        dimSelect('level', level, setLevel, [...BRAND_QUERY_LEVELS], t('bpDash.allLevels'), t('bpDash.filterLevel'))}

    </>
  );

  /**
   * Segmentation stays OUTSIDE the « Filtres » button, on the bar itself.
   *
   * Not an oversight: the product draws a hard line between the two gestures —
   * « Segmenter trace une courbe par valeur, sans rien retirer du périmètre. Filtrer
   * réduit le périmètre mesuré. » Filing it under « Filtres » would teach the
   * opposite of the sentence printed under the bar.
   */
  const segmentControl = (
    <TextField
      select
      size="small"
      label={t('bpDash.segmentBy')}
      value={segmentBy}
      onChange={(e) => setSegmentBy(e.target.value as BPDashboardSegmentBy)}
      sx={{ minWidth: 180 }}
    >
      {SEGMENT_OPTIONS.map((o) => (
        <MenuItem key={o} value={o}>
          {segmentOptionLabel[o]}
        </MenuItem>
      ))}
    </TextField>
  );

  // ---- Général ------------------------------------------------------------

  const trendTitle =
    axis === 'narrative'
      ? t('bpDash.trendNarrative')
      : axis === 'authority'
        ? t('bpDash.trendAuthority')
        : t('bpDash.trendReferral');

  const measuredDays = points.filter((p) => p.score != null).length;
  const breakdownsNote = activeDim ? t('bpDash.caveat.breakdowns_unfiltered') : undefined;

  const generalView = () => {
    if (loading) return <LoadingSkeleton />;
    if (loadError) return <NotePanel title={t('bpDash.loadError')}>{loadError}</NotePanel>;
    if (!data) {
      return (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {emptyNote}
          </Typography>
        </Box>
      );
    }

    type RateField = 'citationRate' | 'recommendationRate' | 'sourceRate';
    const rates: { field: RateField; label: string }[] = (
      [
        { field: 'citationRate', label: t('bpDash.kpiCitationRate') },
        { field: 'recommendationRate', label: t('bpDash.kpiRecommendationRate') },
        { field: 'sourceRate', label: t('bpDash.kpiSourceRate') },
      ] as { field: RateField; label: string }[]
    ).filter((d) => data.current?.[d.field] != null);

    const topics = data.topics;

    // ---- the breakdown rows -------------------------------------------------
    // Each `by*` map gets the rendering its DIMENSION asks for, not a generic one:
    // a mark where the category has a recognisable one (engine logo, language flag),
    // a rank where the order is the message (tags), columns where the categories are
    // an ordered ladder (L1→L5).
    const nResponses = (n: number | undefined) =>
      n != null ? t('bpDash.nResponses', { count: n }) : unmeasured;
    const nPrompts = (n: number | undefined) =>
      n != null ? t('bpDash.nPrompts', { count: n }) : unmeasured;

    const engineRows = scoreRows(data.byEngine, {
      label: (x) => ENGINE_LABELS[x] ?? x,
      mark: engineMark,
      color: engineColor,
      withScale: nResponses,
    });
    const zoneRows = scoreRows(data.byZone, {
      label: (x) => x,
      sub: nResponses,
      color: () => VIZ.violet,
    });
    const languageRows = scoreRows(data.byLanguage, {
      label: (x) => languageLabel(x, i18n.language),
      mark: (x) => <Flag countryCode={languageFlag(x)} size={22} />,
      sub: nResponses,
      color: () => VIZ.serious,
    });
    // Under a tag filter the tag card narrows to the selected tags: `byTag` is keyed by
    // the very dimension being filtered, so each kept bucket IS the filtered measure —
    // unlike the other breakdowns, which cannot be narrowed. Same case-insensitive
    // match as the API's tag filter.
    const byTag = tags.length > 0 && data.byTag
      ? Object.fromEntries(
          Object.entries(data.byTag).filter(([k]) => tags.some((x) => x.toLowerCase() === k.toLowerCase())),
        )
      : data.byTag;
    const tagRows = scoreRows(byTag, {
      label: (x) => x,
      sub: nPrompts,
      color: () => VIZ.violet,
    });
    // EVERY rung, measured or not — the ladder is the chart, so a missing L1 is a
    // hatched column saying « n. m. », never an absent one that shortens the scale.
    const levelColumns = data.byLevel
      ? BRAND_QUERY_LEVELS.map((lvl) => {
          const b = data.byLevel?.[lvl];
          return {
            chip: lvl.toUpperCase(),
            value: b?.score ?? null,
            sub: b?.n != null ? nResponses(b.n) : undefined,
          };
        })
      : [];

    // The mock's `.row`: cards flow two-up and REFLOW to one at 520px, rather than
    // being pinned to two columns that squeeze a bar chart's label gutter to nothing.
    const row = { display: 'flex', flexWrap: 'wrap', gap: `${CARD.gap * 8}px` } as const;
    const cell = { flex: '1 1 520px', minWidth: 0 } as const;
    /** Rate families keep ONE colour across the KPI dot, the bar and the trend line. */
    const rateColor: Record<RateField, string> = {
      citationRate: VIZ.good,
      recommendationRate: VIZ.serious,
      sourceRate: VIZ.violet,
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${CARD.gap * 8}px` }}>
        <NoticePanel title={t('bpDash.caveatsTitle')} lines={caveatLines} />

        {/* ---- KPI row ----------------------------------------------------- */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <StatTile
            sx={{ flex: '1 1 220px' }}
            label={t('bpDash.kpiScore')}
            dot={VIZ.violet}
            // Only `image_v1` rows measured: the per-criterion value below is the reading,
            // so the alignment figure is the bare placeholder, not « Non mesuré ».
            value={
              data.current?.score != null
                ? fmtInt(data.current.score)
                : data.current?.scoreImageV1 != null
                  ? '—'
                  : notMeasured
            }
            // Same shape as the rate tiles beside it. NO bar when the score is null:
            // an empty track reads as a measured zero, and the figure already says
            // « Non mesuré ». The 0-100 scale is `bar`'s default max.
            bar={
              data.current?.score != null
                ? { value: data.current.score, color: VIZ.violet }
                : undefined
            }
            delta={
              scoreDelta(data.current?.score, data.prev?.score) != null
                ? {
                    value: scoreDelta(data.current?.score, data.prev?.score)!,
                    suffix: `pts ${t('bpDash.vsPrevious')}`,
                  }
                : undefined
            }
            foot={
              data.current?.scoreImageV1 != null || hasCaveat('rates_unavailable_under_filter') ? (
                <>
                  {/* F step 8b: the per-criterion score, its own value, never averaged in. */}
                  {data.current?.scoreImageV1 != null && (
                    <Box component="span" sx={{ display: 'block' }}>
                      {t('bpDash.kpiScoreImageV1', { n: fmtInt(data.current.scoreImageV1) })}
                    </Box>
                  )}
                  {hasCaveat('rates_unavailable_under_filter') && t('bpDash.caveat.rates_unavailable_under_filter')}
                </>
              ) : undefined
            }
          />
          {rates.map((r) => (
            <StatTile
              key={r.field}
              sx={{ flex: '1 1 220px' }}
              label={r.label}
              dot={rateColor[r.field]}
              value={fmtPct(data.current![r.field] as number)}
              bar={{ value: (data.current![r.field] as number) * 100, color: rateColor[r.field] }}
              delta={
                pointDelta(data.current?.[r.field], data.prev?.[r.field]) != null
                  ? {
                      value: pointDelta(data.current?.[r.field], data.prev?.[r.field])!,
                      suffix: `pts ${t('bpDash.vsPrevious')}`,
                    }
                  : undefined
              }
            />
          ))}
          <StatTile
            sx={{ flex: '1 1 220px' }}
            label={t('bpDash.kpiPrompts')}
            dot={VIZ.ink}
            value={data.current?.n != null ? fmtInt(data.current.n) : notMeasured}
            foot={t('bpDash.kpiPromptsFoot', { days: fmtInt(measuredDays) })}
          />
        </Box>

        {/* The narrative « Perception IA » lede lives in « Configurer » (2026-10-08),
            off the LAST AUDIT's own summary. It was here, re-presented over the
            window — but it is prose about one run, not a measurement over a period,
            so a 2-year window would have shown a single audit's sentences under a
            two-year heading. The dashboard response still carries `perception`; this
            view simply no longer renders it. */}

        {/* ---- score over time -------------------------------------------- */}
        <ChartCard
          title={trendTitle}
          dot={VIZ.violet}
          hint={t('bpDash.trendHint')}
          {...viewLabels}
          table={
            <VizTable
              columns={[dayCol, ...scoreSeries.map((s) => s.label)]}
              align={['left', ...scoreSeries.map(() => 'right' as const)]}
              rows={bucketLabels.map((lab, i) => [
                lab,
                ...scoreSeries.map((s) => (s.points[i] != null ? fmtInt(s.points[i] as number) : unmeasured)),
              ])}
            />
          }
        >
          <TrendLine
            labels={bucketLabels}
            series={scoreSeries}
            events={ruleBoundary}
            yMax={100}
            format={(v) => fmtInt(v)}
            emptyNote={emptyNote}
            unmeasuredNote={unmeasured}
            // Only when the brand's own line stands alone: stacked translucent
            // areas stop being readable at two, and overlays are a comparison.
            area={scoreSeries.length === 1}
          />
          {(hiddenSegments > 0 || data.partialBuckets?.length) && (
            <Typography sx={{ fontSize: 11.5, color: VIZ.muted, mt: 1 }}>
              {hiddenSegments > 0 && t('bpDash.segmentCap', { n: fmtInt(hiddenSegments) })}
              {!!data.partialBuckets?.length && (
                <>
                  {hiddenSegments > 0 ? ' ' : ''}
                  {t('bpDash.partialNote', {
                    days: data.partialBuckets.map(fmtBucket).join(', '),
                  })}
                </>
              )}
            </Typography>
          )}
        </ChartCard>

        {/* ---- rates over time (own axis) --------------------------------- */}
        {rateSeries.length > 0 && (
          <ChartCard
            title={t('bpDash.ratesTitle')}
            dot={VIZ.good}
            hint={t('bpDash.ratesHint')}
            {...viewLabels}
            table={
              <VizTable
                columns={[dayCol, ...rateSeries.map((s) => s.label)]}
                align={['left', ...rateSeries.map(() => 'right' as const)]}
                rows={bucketLabels.map((lab, i) => [
                  lab,
                  ...rateSeries.map((s) => (s.points[i] != null ? fmtPct(s.points[i] as number) : unmeasured)),
                ])}
              />
            }
          >
            <TrendLine
              labels={bucketLabels}
              series={rateSeries}
              yMax={1}
              emptyNote={emptyNote}
              unmeasuredNote={unmeasured}
            />
          </ChartCard>
        )}

        {/* ---- the two-up card field --------------------------------------
            One flex-wrap row for every remaining card: each one asks for 520px and
            the row decides whether that is two columns or one. A fixed 2-column grid
            squeezed the bar charts' label gutter to nothing on a 13" screen. */}
        <Box sx={row}>
          {/* ---- sentiment (narrative + referral) ------------------------- */}
          {axis !== 'authority' && sentimentRows.length > 0 && (
            <Box sx={cell}>
              <ChartCard
                title={t('bpDash.sentimentTitle')}
                dot={VIZ.serious}
                hint={t('bpDash.sentimentHint')}
                {...viewLabels}
                table={
                  <VizTable
                    columns={[
                      dayCol,
                      t('axisPage.sentiment.positive'),
                      t('axisPage.sentiment.neutral'),
                      t('axisPage.sentiment.negative'),
                      t('bpDash.colResponses'),
                    ]}
                    align={['left', 'right', 'right', 'right', 'right']}
                    rows={sentimentRows.map((r) => [
                      r.label,
                      ...r.parts.map((p) => fmtPct(p.value)),
                      fmtInt(r.n),
                    ])}
                  />
                }
              >
                {/* Columns, not rows: the buckets are a time series, and the shared
                    % axis is what makes a 60-response day comparable to a 50. */}
                <StackedBars
                  rows={sentimentRows}
                  orientation="column"
                  nSuffix={t('bpDash.colResponses').toLowerCase()}
                  emptyNote={emptyNote}
                />
              </ChartCard>
            </Box>
          )}

          {/* ---- alignment (narrative) ------------------------------------ */}
          {axis === 'narrative' && alignmentRows.length > 0 && (
            <Box sx={cell}>
              <ChartCard
                title={t('bpDash.alignmentTitle')}
                dot={VIZ.violet}
                hint={t('bpDash.alignmentHint')}
                {...viewLabels}
                table={
                  <VizTable
                    columns={[
                      dayCol,
                      t('axisPage.alignment.strong'),
                      t('axisPage.alignment.partial'),
                      t('axisPage.alignment.weak'),
                      t('axisPage.alignment.misaligned'),
                      t('bpDash.colResponses'),
                    ]}
                    align={['left', 'right', 'right', 'right', 'right', 'right']}
                    rows={alignmentRows.map((r) => [
                      r.label,
                      ...r.parts.map((p) => fmtPct(p.value)),
                      fmtInt(r.n),
                    ])}
                  />
                }
              >
                <StackedBars
                  rows={alignmentRows}
                  orientation="column"
                  nSuffix={t('bpDash.colResponses').toLowerCase()}
                  emptyNote={emptyNote}
                />
              </ChartCard>
            </Box>
          )}

          {/* ---- authority: topic coverage -------------------------------- */}
          {axis === 'authority' && topics && (topics.cited.length > 0 || topics.missing.length > 0) && (
            <Box sx={cell}>
              <ChartCard
                title={t('bpDash.topicsTitle')}
                dot={VIZ.good}
                hint={t('bpDash.topicsHint')}
                {...viewLabels}
                table={
                  <VizTable
                    columns={[t('bpDash.colTopic'), t('bpDash.colState')]}
                    align={['left', 'left']}
                    rows={[
                      ...topics.cited.map((x) => [x, t('bpDash.topicsCovered')]),
                      ...topics.missing.map((x) => [x, t('bpDash.topicsMissing')]),
                    ]}
                  />
                }
              >
                {/* A single part-of-whole bar, so it stays a ROW: there is no time
                    axis here, and one lone column would read as a bucket of one day. */}
                <StackedBars
                  rows={[
                    {
                      label: t('bpDash.topicsCoverage'),
                      n: topics.cited.length + topics.missing.length,
                      parts: [
                        {
                          key: 'cited',
                          label: t('bpDash.topicsCovered'),
                          value: topics.cited.length / (topics.cited.length + topics.missing.length),
                          color: VIZ.good,
                        },
                        {
                          key: 'missing',
                          label: t('bpDash.topicsMissing'),
                          value: topics.missing.length / (topics.cited.length + topics.missing.length),
                          color: VIZ.warning,
                        },
                      ],
                    },
                  ]}
                  emptyNote={emptyNote}
                />
              </ChartCard>
            </Box>
          )}

          {/* ---- authority: the per-topic RATE §5.2 asks for --------------
              GAP (lot 3): `BPTREND.topics` is two STRING LISTS with no denominator —
              `applyBreakdowns` only unions the day-level sets — so there is no rate to
              plot. Not fabricated from `cited.length`, which would be a coverage share
              wearing a citation-rate label. Needs a per-topic `{cited, n}` bucket on
              the trend row (writer side) before this card can draw anything. */}
          {axis === 'authority' && (
            <Box sx={cell}>
              <ChartCard title={t('bpDash.topicRateTitle')} dot={VIZ.good}>
                <EmptyNote>{t('bpDash.topicRatePending')}</EmptyNote>
              </ChartCard>
            </Box>
          )}

          {/* ---- score par moteur: a LIST, each row in the engine's own colour.
              The mark is the logo and the name moves to the tooltip + the table
              twin (a logo identifies a row faster than six characters of text). */}
          {engineRows.length > 0 && (
            <Box sx={cell}>
              <ChartCard
                title={t('bpDash.byEngineTitle')}
                dot={VIZ.violet}
                hint={breakdownsNote}
                {...viewLabels}
                table={scoreTable(engineRows, t('bpDash.colResponses'), data.byEngine)}
              >
                <BarRows
                  rows={engineRows}
                  maxValue={100}
                  unmeasuredNote={unmeasured}
                  labelWidth={150}
                  valueWidth={150}
                  emptyNote={emptyNote}
                />
              </ChartCard>
            </Box>
          )}

          {/* ---- score par zone ------------------------------------------- */}
          {zoneRows.length > 0 && (
            <Box sx={cell}>
              <ChartCard
                title={t('bpDash.byZoneTitle')}
                dot={VIZ.violet}
                hint={breakdownsNote}
                {...viewLabels}
                table={scoreTable(zoneRows, t('bpDash.colResponses'), data.byZone)}
              >
                <BarRows
                  rows={zoneRows}
                  maxValue={100}
                  unmeasuredNote={unmeasured}
                  labelWidth={190}
                  valueWidth={40}
                  emptyNote={emptyNote}
                />
              </ChartCard>
            </Box>
          )}

          {/* ---- score par langue: the flag is the mark, the name the tooltip.
              A flag is a CONVENTION for a language, not a claim about a country. */}
          {languageRows.length > 0 && (
            <Box sx={cell}>
              <ChartCard
                title={t('bpDash.byLanguageTitle')}
                dot={VIZ.serious}
                hint={breakdownsNote}
                {...viewLabels}
                table={scoreTable(languageRows, t('bpDash.colResponses'), data.byLanguage)}
              >
                <BarRows
                  rows={languageRows}
                  maxValue={100}
                  unmeasuredNote={unmeasured}
                  labelWidth={190}
                  valueWidth={40}
                  emptyNote={emptyNote}
                />
              </ChartCard>
            </Box>
          )}

          {/* ---- score par niveau d'intention: COLUMNS, because L1→L5 is an
              ordered ladder. Sorting it by value would destroy the reading. */}
          {axis === 'referral' && levelColumns.length > 0 && (
            <Box sx={cell}>
              <ChartCard
                title={t('bpDash.byLevelTitle')}
                dot={VIZ.good}
                hint={breakdownsNote}
                {...viewLabels}
                table={
                  <VizTable
                    columns={[t('bpDash.colLabel'), t('bpDash.colScore'), t('bpDash.colResponses')]}
                    align={['left', 'right', 'right']}
                    rows={levelColumns.map((c) => [
                      c.chip,
                      c.value != null ? fmtInt(c.value) : <Box component="span" sx={{ color: VIZ.nodata }}>{unmeasured}</Box>,
                      c.sub ?? <Box component="span" sx={{ color: VIZ.nodata }}>{unmeasured}</Box>,
                    ])}
                  />
                }
              >
                <ColumnBars
                  columns={levelColumns}
                  unmeasuredNote={unmeasured}
                  scaleFrom={t('bpDash.levelScaleFrom')}
                  scaleTo={t('bpDash.levelScaleTo')}
                  emptyNote={emptyNote}
                />
              </ChartCard>
            </Box>
          )}

          {/* ---- score par tag de prompt ---------------------------------- */}
          {tagRows.length > 0 && (
            <Box sx={cell}>
              <ChartCard
                title={t('bpDash.byTagTitle')}
                dot={VIZ.violet}
                hint={activeDim === 'tags' ? undefined : breakdownsNote}
                {...viewLabels}
                table={scoreTable(tagRows, t('bpDash.colPrompts'), byTag)}
              >
                <BarRows
                  rows={tagRows}
                  maxValue={100}
                  unmeasuredNote={unmeasured}
                  labelWidth={190}
                  valueWidth={40}
                  showRank
                  emptyNote={emptyNote}
                />
                {hasCaveat('by_tag_truncated') && (
                  <Typography sx={{ fontSize: 11.5, color: VIZ.muted, mt: 1 }}>
                    {t('bpDash.caveat.by_tag_truncated')}
                  </Typography>
                )}
                {hasCaveat('tags_or_overlap') && (
                  <Typography sx={{ fontSize: 11.5, color: VIZ.muted, mt: 1 }}>
                    {t('bpDash.caveat.tags_or_overlap')}
                  </Typography>
                )}
              </ChartCard>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  // ---- render ------------------------------------------------------------

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          flexWrap: 'wrap',
          p: '8px 10px',
          bgcolor: VIZ.surface,
          borderRadius: '10px',
          border: '1px solid #E6E6E6',
          // The mock's filter row is COMPACT (32px controls, 13px text) because it is
          // chrome, not content: at MUI's default 40px the bar reads as a form the
          // user must fill in before the figures below mean anything. Set once on the
          // container rather than on each of the eight controls inside it.
          '& .MuiInputBase-root': { minHeight: 36 },
          '& .MuiInputBase-input, & .MuiInputLabel-root, & .MuiChip-label': { fontSize: 13 },
        }}
      >
        {/* ONE button instead of five controls. The COUNT is not decoration: a
            narrowing that is active but out of sight changes every figure on the
            page, so the button carries it, the chips name it, and clearing is
            reachable without opening the popover. */}
        <Button
          size="small"
          variant={activeDim ? 'contained' : 'outlined'}
          startIcon={<FilterListIcon />}
          onClick={(e) => setFilterAnchor(e.currentTarget)}
          sx={{ textTransform: 'none', height: 36 }}
        >
          {activeFilterCount > 0
            ? t('bpDash.filtersButtonActive', { count: activeFilterCount })
            : t('bpDash.filtersButton')}
        </Button>
        {activeFilterChips.map((c) => (
          <Chip key={c.key} size="small" label={c.label} onDelete={c.clear} />
        ))}
        <Popover
          open={!!filterAnchor}
          anchorEl={filterAnchor}
          onClose={() => setFilterAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          slotProps={{ paper: { sx: { p: 2, borderRadius: 2, maxWidth: 420 } } }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ fontSize: 12.5, color: VIZ.muted }}>
              {t('bpDash.oneFilterOnly')}
            </Typography>
            {generalFilters}
            {activeDim && (
              <Button size="small" onClick={clearFilters} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
                {t('bpDash.clearFilters')}
              </Button>
            )}
          </Box>
        </Popover>
        {segmentControl}
        {/* ONE period control for the whole view, capped at the retention the data
            layer actually keeps. Past 90 days the response switches to the monthly
            tier, where only the trend line and the KPIs have rows — see the header. */}
        <Box sx={{ ml: 'auto' }}>
          <TimeframeSelector value={timeframe} onChange={setTimeframe} maxDays={MAX_SPAN_DAYS} />
        </Box>
      </Box>
      {/* The mock's `.top-note`: deliberately small. It is a standing caveat about how
          to read the view, not a message — at body size it competes with the figures. */}
      <Typography sx={{ display: 'block', mt: 1.5, mb: 2.5, fontSize: 11, lineHeight: 1.5, color: '#444' }}>
        {activeDim ? `${t('bpDash.oneFilterOnly')} ` : ''}
        {t('bpDash.segmentHint')}
      </Typography>

      {generalView()}
    </Box>
  );
}
